import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { venueManagerAPI } from '../lib/api';
import { toast } from 'sonner';
import { MapPin, Calendar, Users } from 'lucide-react';

export default function VenueManagerView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [token]);

  const fetchReport = async () => {
    try {
      const response = await venueManagerAPI.getReport(token);
      setData(response.data);
    } catch (error) {
      toast.error('Token non valido o report non disponibile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Report Non Trovato</h1>
          <p className="text-zinc-400">Il token fornito non è valido o è scaduto.</p>
        </div>
      </div>
    );
  }

  const totalVenueCost = data.lessons.reduce((sum, lesson) => sum + lesson.venue_cost, 0);

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="bg-zinc-900/50 border-b border-white/10 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-black text-[#ccff00]">Trainers</h1>
          <p className="text-zinc-400 text-sm">Report Gestore Struttura</p>
        </div>
      </div>

      <div data-testid="venue-manager-view" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
        <div className="glass-card p-8 rounded-sm border border-white/10 mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="w-10 h-10 text-[#ccff00]" />
            <div>
              <h2 className="text-3xl font-black text-white">{data.venue.name}</h2>
              <p className="text-zinc-400">Report affitti pista</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-zinc-900/50 p-4 rounded-sm border border-white/10">
              <div className="text-zinc-400 text-sm mb-1">Totale Lezioni</div>
              <div className="text-3xl font-black text-white">{data.lessons.length}</div>
            </div>
            <div className="bg-zinc-900/50 p-4 rounded-sm border border-white/10">
              <div className="text-zinc-400 text-sm mb-1">Costo Fisso Orario</div>
              <div className="text-3xl font-black text-white">€{data.venue.fixed_cost_per_hour}</div>
            </div>
            <div className="bg-zinc-900/50 p-4 rounded-sm border border-white/10">
              <div className="text-zinc-400 text-sm mb-1">Totale da Ricevere</div>
              <div className="text-3xl font-black text-[#ccff00]">€{totalVenueCost.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-sm border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-2xl font-bold text-white">Dettaglio Lezioni</h3>
          </div>

          {data.lessons.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-zinc-400 text-lg">Nessuna lezione disponibile</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#ccff00] text-black">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Data</th>
                    <th className="px-4 py-3 text-left font-bold">Orario</th>
                    <th className="px-4 py-3 text-left font-bold">Durata</th>
                    <th className="px-4 py-3 text-center font-bold">N. Atleti</th>
                    <th className="px-4 py-3 text-left font-bold">Atleti</th>
                    <th className="px-4 py-3 text-right font-bold">Costo Struttura</th>
                    <th className="px-4 py-3 text-right font-bold">€/Atleta</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lessons.map((lesson, idx) => (
                    <tr
                      key={idx}
                      data-testid="venue-manager-lesson-row"
                      className="border-b border-white/10 hover:bg-zinc-900/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-white">{lesson.date}</td>
                      <td className="px-4 py-3 text-white">{lesson.start_time}</td>
                      <td className="px-4 py-3 text-zinc-300">{lesson.duration_minutes} min</td>
                      <td className="px-4 py-3 text-center text-white">{lesson.num_athletes}</td>
                      <td className="px-4 py-3 text-zinc-300 text-sm">
                        {lesson.athlete_names.join(', ')}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-mono font-bold">€{lesson.venue_cost.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#ccff00] font-mono font-bold">€{lesson.cost_per_athlete.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}